import type { FixAction, Issue } from '@qcbot/shared';
import { FixDriftError } from './fixErrors';

/** OOXML/EMU is the unit `FixAction.position`/`shapeGeometry` are computed in (matching
 * DeckShape's own offset/extent) — PowerPoint.js shape positions use points. */
const EMU_PER_POINT = 12700;

/** Snapshots identify shapes by the live Office.js `Shape.id` captured when the fix was applied —
 * stable for the open session regardless of names, so Undo always finds the shape it edited. */
export type FixSnapshot =
  /** A replaced span: `newText` now sits at `start`; undo puts `oldText` back in its place. */
  | { kind: 'span'; slideNumber: number; liveShapeId: string; start: number; oldText: string; newText: string }
  | { kind: 'fontSize'; slideNumber: number; liveShapeId: string; start: number; length: number; oldSizePt: number | null }
  /** Steps applied in order; undo reverses them in reverse order. `skipped` lists steps that
   * couldn't be applied (e.g. one slide edited since QC) — the rest were still applied. */
  | { kind: 'composite'; snapshots: FixSnapshot[]; skipped: { slideNumber: number; reason: string }[] }
  | { kind: 'cells'; slideNumber: number; liveShapeId: string; entries: { rowIndex: number; cellIndex: number; oldText: string }[] }
  | { kind: 'font'; entries: { slideNumber: number; liveShapeId: string; oldTypeface: string | null }[] }
  | { kind: 'insertedShape'; slideNumber: number; liveShapeId: string };

/** Applies one issue's `fixAction` to the live, open presentation. Throws `FixDriftError` if the
 * exact text/shape it expects is no longer present (deck edited since QC ran) rather than
 * guessing. Returns a snapshot sufficient to reverse the change via `undoFix`. */
export async function applyFix(issue: Issue): Promise<FixSnapshot> {
  const action = issue.fixAction;
  if (!action) throw new Error('No fix is available for this issue.');
  if (action.type === 'setFontTypeface') {
    return applySetFontTypeface(issue.affectedSlides ?? [issue.target.slideNumber], issue.affectedShapeNames ?? [], action);
  }
  return applyAction(issue.target.slideNumber, action);
}

async function applyAction(slideNumber: number, action: FixAction): Promise<FixSnapshot> {
  switch (action.type) {
    case 'replaceText':
      return applyReplaceText(slideNumber, action);
    case 'setCellText':
      return applySetCellText(slideNumber, action);
    case 'insertTextbox':
      return applyInsertTextbox(slideNumber, action);
    case 'setFontSize':
      return applySetFontSize(slideNumber, action);
    case 'composite':
      return applyComposite(action);
    default:
      throw new Error(`Unsupported fix type: ${action.type}`);
  }
}

/** Reverses a previously-applied fix using the snapshot `applyFix` returned. */
export async function undoFix(snapshot: FixSnapshot): Promise<void> {
  switch (snapshot.kind) {
    case 'span':
      return undoReplaceText(snapshot);
    case 'fontSize':
      return undoSetFontSize(snapshot);
    case 'composite':
      return undoComposite(snapshot.snapshots);
    case 'cells':
      return undoSetCellText(snapshot);
    case 'font':
      return undoSetFontTypeface(snapshot);
    case 'insertedShape':
      return undoInsertTextbox(snapshot);
  }
}

// ---------------------------------------------------------------------------------------------
// Shape resolution
//
// A shape's name in the file (`<p:cNvPr name>`) does not reliably equal what Office.js reports
// for it live, so a fix never depends on the name alone. Every shape on the slide (group children
// included) is scored on each identity signal the backend recorded — name, normalised name, OOXML
// id, position/size — and the fix goes to the highest-scoring shape whose LIVE CONTENT passes the
// fix's own exact check (find text present exactly once / every target cell reading exactly what
// QC saw). Content is the gate: identity signals only rank candidates, so a wrong-looking match
// can never be edited, and a shape whose name changed is still found by what it contains.
// ---------------------------------------------------------------------------------------------

type ShapeRef = Pick<FixAction, 'shapeName' | 'shapeId' | 'shapeGeometry'>;

interface LiveShape {
  shape: PowerPoint.Shape;
  /** Position/size in points, when the host reported it. */
  geometry?: { left: number; top: number; width: number; height: number };
}

/** Shape types that can carry a text frame. Shapes of other types are only asked for text when an
 * identity signal points at them (asking e.g. a picture for its text frame fails the batch).
 * Built on first use: the `PowerPoint` global only exists when running inside PowerPoint. */
let textShapeTypes: Set<string> | undefined;
function getTextShapeTypes(): Set<string> {
  textShapeTypes ??= new Set<string>([
    PowerPoint.ShapeType.geometricShape,
    PowerPoint.ShapeType.textBox,
    PowerPoint.ShapeType.placeholder,
    PowerPoint.ShapeType.callout,
    PowerPoint.ShapeType.freeform,
  ]);
  return textShapeTypes;
}

/** How close (points) a live shape's position must be to the parsed one to count as a match. */
const GEOMETRY_TOLERANCE_PT = 1.5;
/** A name (exact or normalised) or position match — enough to say "this is the shape QC meant". */
const STRONG_IDENTITY_SCORE = 2;

async function getSlide(context: PowerPoint.RequestContext, slideNumber: number): Promise<PowerPoint.Slide> {
  const slides = context.presentation.slides;
  slides.load('items/id');
  await context.sync();
  const slide = slides.items[slideNumber - 1];
  if (!slide) throw new FixDriftError(`Slide ${slideNumber} no longer exists — the deck may have changed. Please re-run QC.`);
  return slide;
}

/** `slide.shapes` only lists TOP-LEVEL shapes; the backend's parser flattens group children into
 * its shape list, so the live tree is flattened the same way. */
async function listShapes(context: PowerPoint.RequestContext, slide: PowerPoint.Slide): Promise<LiveShape[]> {
  const out: LiveShape[] = [];
  await collectShapes(context, slide.shapes, out);
  return out;
}

async function collectShapes(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.ShapeCollection | PowerPoint.ShapeScopedCollection,
  out: LiveShape[],
): Promise<void> {
  let withGeometry = true;
  try {
    shapes.load('items/id,items/name,items/type,items/left,items/top,items/width,items/height');
    await context.sync();
  } catch {
    // Some hosts/shape kinds refuse geometry — identity then rests on name/id/content alone.
    withGeometry = false;
    shapes.load('items/id,items/name,items/type');
    await context.sync();
  }
  for (const shape of shapes.items) {
    out.push({
      shape,
      geometry: withGeometry ? { left: shape.left, top: shape.top, width: shape.width, height: shape.height } : undefined,
    });
    if (shape.type === PowerPoint.ShapeType.group) await collectShapes(context, shape.group.shapes, out);
  }
}

function normalizeName(name: string): string {
  return name.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Higher = more identity signals agree. 0 = nothing but (possibly) content links it to the fix. */
function scoreShape(live: LiveShape, ref: ShapeRef): number {
  let score = 0;
  const { shape, geometry } = live;
  if (ref.shapeName) {
    if (shape.name === ref.shapeName) score += 4;
    else if (normalizeName(shape.name) === normalizeName(ref.shapeName)) score += 3;
  }
  // Office.js's id format isn't documented as the OOXML id; a trailing-number match is only a hint.
  if (ref.shapeId !== undefined && Number(/(\d+)$/.exec(shape.id)?.[1]) === ref.shapeId) score += 1;
  if (ref.shapeGeometry && geometry) {
    const g = ref.shapeGeometry;
    const near = (pt: number, emu: number) => Math.abs(pt - emu / EMU_PER_POINT) <= GEOMETRY_TOLERANCE_PT;
    if (near(geometry.left, g.leftEmu) && near(geometry.top, g.topEmu)) {
      score += near(geometry.width, g.widthEmu) && near(geometry.height, g.heightEmu) ? 3 : 2;
    }
  }
  return score;
}

/** Loads each shape's text; a shape that can't report text yields undefined instead of failing
 * the whole lookup. */
async function loadTexts(context: PowerPoint.RequestContext, shapes: PowerPoint.Shape[]): Promise<(string | undefined)[]> {
  const ranges = shapes.map((s) => {
    const range = s.textFrame.textRange;
    range.load('text');
    return range;
  });
  try {
    await context.sync();
    return ranges.map((r) => normalizeBreaks(r.text ?? ''));
  } catch {
    const texts: (string | undefined)[] = [];
    for (const shape of shapes) {
      try {
        const range = shape.textFrame.textRange;
        range.load('text');
        await context.sync();
        texts.push(normalizeBreaks(range.text ?? ''));
      } catch {
        texts.push(undefined);
      }
    }
    return texts;
  }
}

/** Picks the highest-scoring content match; refuses a tie, since content can't say which one. */
function pickBest<T extends { score: number }>(matches: T[], slideNumber: number, what: string): T {
  const sorted = [...matches].sort((a, b) => b.score - a.score);
  if (sorted.length > 1 && sorted[0].score === sorted[1].score) {
    throw new FixDriftError(`Couldn't apply: ${what} appears in ${sorted.length} shapes on slide ${slideNumber}, so it's ambiguous which one to change. Please fix by hand.`);
  }
  return sorted[0];
}

function describeShapes(all: LiveShape[]): string {
  const names = all.map((l) => `"${l.shape.name}"`);
  return names.slice(0, 12).join(', ') + (names.length > 12 ? `, +${names.length - 12} more` : '') || 'none';
}

/** Resolves the one live shape whose text contains `findText` exactly once, ranked by identity. */
async function resolveTextShape(
  context: PowerPoint.RequestContext,
  slideNumber: number,
  ref: ShapeRef,
  findText: string,
): Promise<{ shape: PowerPoint.Shape; start: number }> {
  const slide = await getSlide(context, slideNumber);
  const all = await listShapes(context, slide);
  const candidates = all
    .map((live) => ({ live, score: scoreShape(live, ref) }))
    .filter((c) => getTextShapeTypes().has(c.live.shape.type) || c.score > 0);
  const texts = await loadTexts(context, candidates.map((c) => c.live.shape));
  const scored = candidates.map((c, i) => ({ ...c, text: texts[i] }));

  const matches = scored.filter((c) => c.text !== undefined && countOccurrences(c.text, findText) === 1);
  // A content-only match (no identity signal at all) is accepted only when nothing on the slide is
  // positively identified as the target. If the target IS identified (name or position) but no
  // longer contains the text, the slide was edited — don't move the edit to some other shape that
  // happens to contain the same words.
  const targetIdentified = scored.some((c) => c.score >= STRONG_IDENTITY_SCORE);
  const usable = targetIdentified ? matches.filter((c) => c.score > 0) : matches;
  if (usable.length === 0) {
    // Explain against the shape the identity signals point at, if any — that's the one QC meant.
    const intended = scored.filter((c) => c.text !== undefined && c.score > 0).sort((a, b) => b.score - a.score)[0];
    const reason = intended
      ? diagnoseTextMismatch(intended.text!, findText)
      : `the text "${findText}" was not found in any shape on this slide (shapes: ${describeShapes(all)})`;
    throw new FixDriftError(`Couldn't apply: ${reason}. Please re-run QC to refresh this fix.`);
  }
  const best = pickBest(usable, slideNumber, `"${findText}"`);
  return { shape: best.live.shape, start: best.text!.indexOf(findText) };
}

async function resolveShapeByLiveId(context: PowerPoint.RequestContext, slideNumber: number, liveShapeId: string): Promise<PowerPoint.Shape> {
  const slide = await getSlide(context, slideNumber);
  const found = (await listShapes(context, slide)).find((l) => l.shape.id === liveShapeId);
  if (!found) throw new FixDriftError(`Couldn't undo: the edited shape on slide ${slideNumber} no longer exists.`);
  return found.shape;
}

// ---------------------------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------------------------

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
}

/** Distinguishes "the deck genuinely changed" from "the live text differs from what QC saw in a
 * way that hints at a real bug" (case/whitespace normalization differences between how the
 * backend parses OOXML text and how Office.js reports live TextRange text) — so a report of this
 * error is actually debuggable instead of a dead end. */
function diagnoseTextMismatch(haystack: string, needle: string): string {
  const count = countOccurrences(haystack, needle);
  if (count > 1) return `"${needle}" appears ${count} times in this shape, so it's ambiguous which one to change`;
  if (countOccurrences(haystack.toLowerCase(), needle.toLowerCase()) > 0) {
    return `text matching "${needle}" was found but with different capitalization`;
  }
  const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
  if (countOccurrences(normalize(haystack), normalize(needle)) > 0) {
    return `text matching "${needle}" was found but with different spacing/line breaks`;
  }
  return `the exact text "${needle}" is no longer present on this slide`;
}

/** The backend's parsed text separates paragraphs with "\n"; Office.js reports "\r" (or "\v" for
 * a soft line break). Mapping each separator character to one "\n" keeps every index 1:1 with the
 * live text range, so indices found here are valid for `getSubstring`. */
function normalizeBreaks(text: string): string {
  return text.replace(/[\r\v]/g, '\n');
}

// ---------------------------------------------------------------------------------------------
// Fix types
// ---------------------------------------------------------------------------------------------

async function applyReplaceText(slideNumber: number, action: FixAction): Promise<FixSnapshot> {
  if (!action.findText || action.replaceWith === undefined) throw new Error('Incomplete fix action.');
  let snapshot!: FixSnapshot;
  await PowerPoint.run(async (context) => {
    const { shape, start } = await resolveTextShape(context, slideNumber, action, action.findText!);
    // Only the matched span is rewritten, so the rest of the shape keeps its per-run formatting
    // (bold, colour, size) — assigning the whole `textRange.text` used to flatten all of it.
    shape.textFrame.textRange.getSubstring(start, action.findText!.length).text = action.replaceWith!;
    await context.sync();
    snapshot = { kind: 'span', slideNumber, liveShapeId: shape.id, start, oldText: action.findText!, newText: action.replaceWith! };
  });
  return snapshot;
}

async function undoReplaceText(snapshot: Extract<FixSnapshot, { kind: 'span' }>): Promise<void> {
  await PowerPoint.run(async (context) => {
    const shape = await resolveShapeByLiveId(context, snapshot.slideNumber, snapshot.liveShapeId);
    const textRange = shape.textFrame.textRange;
    textRange.load('text');
    await context.sync();
    const liveText = normalizeBreaks(textRange.text);
    if (liveText.slice(snapshot.start, snapshot.start + snapshot.newText.length) !== snapshot.newText) {
      throw new FixDriftError("Couldn't undo: this text has been edited since the fix was applied.");
    }
    if (snapshot.newText.length > 0) {
      textRange.getSubstring(snapshot.start, snapshot.newText.length).text = snapshot.oldText;
    } else if (snapshot.start > 0) {
      // The fix deleted text, leaving nothing to select — re-insert by rewriting the character before it.
      textRange.getSubstring(snapshot.start - 1, 1).text = liveText[snapshot.start - 1] + snapshot.oldText;
    } else {
      textRange.getSubstring(0, 1).text = snapshot.oldText + liveText[0];
    }
    await context.sync();
  });
}

async function applySetFontSize(slideNumber: number, action: FixAction): Promise<FixSnapshot> {
  if (!action.findText || action.rangeOffset === undefined || !action.rangeLength || !action.fontSizePt) {
    throw new Error('Incomplete fix action.');
  }
  let snapshot!: FixSnapshot;
  await PowerPoint.run(async (context) => {
    const { shape, start: anchorStart } = await resolveTextShape(context, slideNumber, action, action.findText!);
    const start = anchorStart + action.rangeOffset!;
    const range = shape.textFrame.textRange.getSubstring(start, action.rangeLength!);
    range.font.load('size');
    await context.sync();
    const oldSizePt = range.font.size;
    range.font.size = action.fontSizePt!;
    await context.sync();
    snapshot = { kind: 'fontSize', slideNumber, liveShapeId: shape.id, start, length: action.rangeLength!, oldSizePt };
  });
  return snapshot;
}

async function undoSetFontSize(snapshot: Extract<FixSnapshot, { kind: 'fontSize' }>): Promise<void> {
  // null = the span already mixed sizes before the fix; there's no single size to restore (same
  // documented limitation as setFontTypeface's undo).
  if (snapshot.oldSizePt === null) return;
  await PowerPoint.run(async (context) => {
    const shape = await resolveShapeByLiveId(context, snapshot.slideNumber, snapshot.liveShapeId);
    shape.textFrame.textRange.getSubstring(snapshot.start, snapshot.length).font.size = snapshot.oldSizePt!;
    await context.sync();
  });
}

/** Applies every step it can, in order. One stale slide (edited since QC) no longer blocks the
 * same fix on every other slide: failed steps are reported in `skipped` for the reviewer to handle
 * by hand, and Undo reverses exactly the steps applied. Only when NO step applies does this throw. */
async function applyComposite(action: FixAction): Promise<FixSnapshot> {
  const steps = action.steps ?? [];
  if (steps.length === 0) throw new Error('Incomplete fix action.');
  const applied: FixSnapshot[] = [];
  const skipped: { slideNumber: number; reason: string }[] = [];
  let firstError: unknown;
  for (const step of steps) {
    try {
      applied.push(await applyAction(step.slideNumber, step.action));
    } catch (error) {
      firstError ??= error;
      skipped.push({ slideNumber: step.slideNumber, reason: error instanceof Error ? error.message : String(error) });
    }
  }
  if (applied.length === 0) {
    const message = `Slide ${skipped[0].slideNumber}: ${skipped[0].reason}`;
    throw firstError instanceof FixDriftError ? new FixDriftError(message) : new Error(message);
  }
  return { kind: 'composite', snapshots: applied, skipped };
}

async function undoComposite(snapshots: FixSnapshot[]): Promise<void> {
  for (const snapshot of [...snapshots].reverse()) {
    await undoFix(snapshot);
  }
}

async function applySetCellText(slideNumber: number, action: FixAction): Promise<FixSnapshot> {
  if (!action.cells || action.cells.length === 0) throw new Error('Incomplete fix action.');
  const edits = action.cells;
  let snapshot!: FixSnapshot;
  await PowerPoint.run(async (context) => {
    const slide = await getSlide(context, slideNumber);
    const all = await listShapes(context, slide);
    // Every table on the slide is a candidate; its cells (not its name) decide whether it's the one.
    const tables = all
      .filter((l) => l.shape.type === PowerPoint.ShapeType.table)
      .map((live) => {
        const table = live.shape.getTable();
        const cells = edits.map((c) => table.getCellOrNullObject(c.rowIndex, c.cellIndex));
        cells.forEach((cell) => cell.load('text'));
        return { live, score: scoreShape(live, action), cells };
      });
    await context.sync();

    // Every cell must still read exactly what QC saw before ANY is changed — a multi-cell fix
    // applies as one all-or-nothing unit, never partially.
    const cellsMatch = (cells: PowerPoint.TableCell[]) =>
      cells.every((cell, i) => !cell.isNullObject && cell.text.trim() === edits[i].findText.trim());
    const matches = tables.filter((t) => cellsMatch(t.cells));
    if (matches.length === 0) {
      const intended = [...tables].sort((a, b) => b.score - a.score)[0];
      const bad = intended?.cells.findIndex((cell, i) => cell.isNullObject || cell.text.trim() !== edits[i].findText.trim());
      const reason =
        intended && bad !== undefined && bad >= 0
          ? `expected cell (row ${edits[bad].rowIndex + 1}, col ${edits[bad].cellIndex + 1}) to read "${edits[bad].findText}" but found "${
              intended.cells[bad].isNullObject ? '(cell no longer exists)' : intended.cells[bad].text.trim()
            }"`
          : `no table on slide ${slideNumber} has the expected cell values`;
      throw new FixDriftError(`Couldn't apply: ${reason}. Please re-run QC to refresh this fix.`);
    }
    const best = pickBest(matches, slideNumber, 'the expected table');
    const entries = edits.map((c, i) => ({ rowIndex: c.rowIndex, cellIndex: c.cellIndex, oldText: best.cells[i].text }));
    best.cells.forEach((cell, i) => {
      cell.text = edits[i].replaceWith;
    });
    await context.sync();
    snapshot = { kind: 'cells', slideNumber, liveShapeId: best.live.shape.id, entries };
  });
  return snapshot;
}

async function undoSetCellText(snapshot: Extract<FixSnapshot, { kind: 'cells' }>): Promise<void> {
  await PowerPoint.run(async (context) => {
    const shape = await resolveShapeByLiveId(context, snapshot.slideNumber, snapshot.liveShapeId);
    const table = shape.getTable();
    for (const entry of snapshot.entries) {
      table.getCellOrNullObject(entry.rowIndex, entry.cellIndex).text = entry.oldText;
    }
    await context.sync();
  });
}

async function applySetFontTypeface(affectedSlides: number[], affectedShapeNames: string[], action: FixAction): Promise<FixSnapshot> {
  if (!action.fontTypeface || affectedShapeNames.length === 0) throw new Error('Incomplete fix action.');
  // null means the shape already mixed multiple fonts before this fix — undo can't perfectly
  // restore a per-run mix from one stored string, so those shapes just keep the dominant font
  // after an undo (documented limitation, not silently wrong data).
  const entries: { slideNumber: number; liveShapeId: string; oldTypeface: string | null }[] = [];
  const wanted = new Set(affectedShapeNames.map(normalizeName));
  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();

    // This deck-wide fix carries only shape names (no per-shape text to verify against), so it
    // matches on exact or normalised name; shapes whose name doesn't round-trip are left alone.
    const targets: { slideNumber: number; shape: PowerPoint.Shape }[] = [];
    for (const slideNumber of affectedSlides) {
      const slide = slides.items[slideNumber - 1];
      if (!slide) continue;
      for (const live of await listShapes(context, slide)) {
        if (wanted.has(normalizeName(live.shape.name)) && getTextShapeTypes().has(live.shape.type)) targets.push({ slideNumber, shape: live.shape });
      }
    }
    if (targets.length === 0) {
      throw new FixDriftError('None of the flagged shapes were found — the deck may have changed. Please re-run QC.');
    }

    targets.forEach(({ shape }) => shape.textFrame.textRange.font.load('name'));
    await context.sync();
    targets.forEach(({ slideNumber, shape }) => {
      entries.push({ slideNumber, liveShapeId: shape.id, oldTypeface: shape.textFrame.textRange.font.name });
      shape.textFrame.textRange.font.name = action.fontTypeface!;
    });
    await context.sync();
  });
  return { kind: 'font', entries };
}

async function undoSetFontTypeface(snapshot: Extract<FixSnapshot, { kind: 'font' }>): Promise<void> {
  await PowerPoint.run(async (context) => {
    for (const entry of snapshot.entries) {
      if (!entry.oldTypeface) continue;
      const shape = await resolveShapeByLiveId(context, entry.slideNumber, entry.liveShapeId).catch(() => undefined);
      if (shape) shape.textFrame.textRange.font.name = entry.oldTypeface;
    }
    await context.sync();
  });
}

async function applyInsertTextbox(slideNumber: number, action: FixAction): Promise<FixSnapshot> {
  if (!action.insertText) throw new Error('Incomplete fix action.');
  let snapshot!: FixSnapshot;
  await PowerPoint.run(async (context) => {
    const slide = await getSlide(context, slideNumber);

    const position = action.position
      ? {
          left: action.position.leftEmu / EMU_PER_POINT,
          top: action.position.topEmu / EMU_PER_POINT,
          width: action.position.widthEmu / EMU_PER_POINT,
          height: action.position.heightEmu / EMU_PER_POINT,
        }
      : // Generic bottom-left default when no shape geometry was resolvable (e.g. slide-level
        // footnote fixes) — approximates a 16:9 widescreen slide's footer area in points.
        { left: 36, top: 500, width: 648, height: 24 };

    const shape = slide.shapes.addTextBox(action.insertText!, position);
    shape.name = `QCBot Fix ${Date.now()}`;
    shape.load('id');
    await context.sync();
    snapshot = { kind: 'insertedShape', slideNumber, liveShapeId: shape.id };
  });
  return snapshot;
}

async function undoInsertTextbox(snapshot: Extract<FixSnapshot, { kind: 'insertedShape' }>): Promise<void> {
  await PowerPoint.run(async (context) => {
    const shape = await resolveShapeByLiveId(context, snapshot.slideNumber, snapshot.liveShapeId);
    shape.delete();
    await context.sync();
  });
}
